import React, { useMemo, useState, useEffect } from 'react';
import type {
  BillingClient,
  BusinessInfo,
  ClientInvoice,
  InvoiceLineItem,
  InvoicePaymentStatus,
  PoGstMode,
  PurchasingInvoiceDefaults,
} from '../../types';
import TrashIcon from '../icons/TrashIcon';
import PlusIcon from '../icons/PlusIcon';
import XCircleIcon from '../icons/XCircleIcon';
import { computePoTotals } from '../../lib/nzGst';
import { newInvoiceLine, suggestClientInvoiceNumber } from '../../lib/invoiceUtils';
import {
  hasBlockingIssues,
  validateClientInvoiceRecord,
} from '../../lib/purchasingInvoiceValidation';
import { generateClientInvoicePdf } from '../../lib/clientInvoicePdf';
import { buildClientInvoiceViewModel, enrichClientInvoiceViewModelForPrint, type ClientInvoiceViewModel } from '../../lib/clientInvoiceTemplate';
import ClientInvoicePreviewModal from './ClientInvoicePreviewModal';
import InvoiceLineNumericFields from './InvoiceLineNumericFields';

interface ClientInvoiceBuilderProps {
  invoice: ClientInvoice | null;
  billingClients: BillingClient[];
  initialClientId?: number;
  businessInfo?: BusinessInfo | null;
  purchasingDefaults?: PurchasingInvoiceDefaults;
  buffetBreakfastPrice?: number;
  staffId?: number;
  onClose: () => void;
  onSave: (invoice: ClientInvoice) => void | Promise<void>;
}

function toDateInput(ms?: number): string {
  const d = ms ? new Date(ms) : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseDateInput(value: string): number {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d).getTime();
}

const ClientInvoiceBuilder: React.FC<ClientInvoiceBuilderProps> = ({
  invoice,
  billingClients,
  initialClientId,
  businessInfo,
  purchasingDefaults,
  buffetBreakfastPrice,
  staffId,
  onClose,
  onSave,
}) => {
  const defaultGst = purchasingDefaults?.defaultGstMode ?? 'inclusive';
  const [clientId, setClientId] = useState(billingClients[0]?.id ?? 0);
  const [invoiceNumber, setInvoiceNumber] = useState(suggestClientInvoiceNumber());
  const [invoiceDate, setInvoiceDate] = useState(toDateInput());
  const [dueDate, setDueDate] = useState(toDateInput());
  const [hasDueDate, setHasDueDate] = useState(false);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [gstMode, setGstMode] = useState<PoGstMode>(defaultGst);
  const [paymentStatus, setPaymentStatus] = useState<InvoicePaymentStatus>('unpaid');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<InvoiceLineItem[]>([newInvoiceLine()]);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewViewModel, setPreviewViewModel] = useState<ClientInvoiceViewModel | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const client = billingClients.find((c) => c.id === clientId);

  useEffect(() => {
    if (invoice) {
      setClientId(invoice.clientId);
      setInvoiceNumber(invoice.invoiceNumber);
      setInvoiceDate(toDateInput(invoice.invoiceDate));
      setDueDate(toDateInput(invoice.dueDate));
      setHasDueDate(Boolean(invoice.dueDate));
      setPeriodStart(invoice.periodStart ? toDateInput(invoice.periodStart) : '');
      setPeriodEnd(invoice.periodEnd ? toDateInput(invoice.periodEnd) : '');
      setGstMode(invoice.gstMode);
      setPaymentStatus(invoice.paymentStatus);
      setNotes(invoice.notes ?? '');
      setItems(invoice.items.length ? invoice.items : [newInvoiceLine()]);
      return;
    }
    if (initialClientId) setClientId(initialClientId);
  }, [invoice, initialClientId]);

  useEffect(() => {
    if (invoice || items.some((l) => l.description.trim())) return;
    const price =
      client?.defaultBreakfastCoverPrice ?? buffetBreakfastPrice ?? 0;
    if (client && price > 0) {
      setItems([
        newInvoiceLine({
          description: 'Breakfast covers',
          quantity: 1,
          unit: 'covers',
          unitPrice: price,
        }),
      ]);
    }
  }, [client, invoice, items, buffetBreakfastPrice]);

  const poShape = useMemo(
    () =>
      items.map((line) => ({
        lineId: line.lineId,
        productName: line.description,
        quantity: line.quantity,
        costPerUnit: line.unitPrice,
        unit: 'units' as const,
      })),
    [items],
  );
  const totals = useMemo(() => computePoTotals(poShape, gstMode), [poShape, gstMode]);

  const draftInvoice: ClientInvoice = {
    id: invoice?.id ?? '',
    clientId,
    invoiceNumber,
    invoiceDate: parseDateInput(invoiceDate),
    dueDate: hasDueDate ? parseDateInput(dueDate) : undefined,
    periodStart: periodStart ? parseDateInput(periodStart) : undefined,
    periodEnd: periodEnd ? parseDateInput(periodEnd) : undefined,
    items,
    gstMode,
    paymentStatus,
    notes: notes.trim() || undefined,
    dateCreated: invoice?.dateCreated ?? Date.now(),
    recordedBy: staffId,
  };

  const validationIssues = useMemo(
    () => validateClientInvoiceRecord(draftInvoice, client, businessInfo),
    [draftInvoice, client, businessInfo],
  );

  const canPreview = Boolean(client && businessInfo && !hasBlockingIssues(validationIssues));

  const openPreview = async () => {
    if (!client || !businessInfo || hasBlockingIssues(validationIssues)) return;
    setPreviewLoading(true);
    try {
      const baseVm = buildClientInvoiceViewModel({
        invoice: draftInvoice,
        client,
        businessInfo,
        defaults: purchasingDefaults,
      });
      setPreviewViewModel(await enrichClientInvoiceViewModelForPrint(baseVm));
      setPreviewOpen(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  const updateLine = (index: number, patch: Partial<InvoiceLineItem>) => {
    setItems((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hasBlockingIssues(validationIssues)) {
      alert(validationIssues.filter((i) => i.level === 'error').map((i) => i.message).join('\n'));
      return;
    }
    setSaving(true);
    try {
      await onSave(draftInvoice);
    } finally {
      setSaving(false);
    }
  };

  const handlePdf = async () => {
    if (!client || !businessInfo) return;
    setPdfLoading(true);
    try {
      await generateClientInvoicePdf({
        invoice: draftInvoice,
        client,
        businessInfo,
        defaults: purchasingDefaults,
      });
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" data-soft-keyboard-handled>
      <div className="flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-bold">
            {invoice ? 'Edit client invoice' : 'New client tax invoice'}
          </h2>
          <button type="button" onClick={onClose}>
            <XCircleIcon className="h-7 w-7 text-gray-500" />
          </button>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-1 flex-col overflow-hidden">
          <div className="space-y-4 overflow-y-auto px-6 py-4">
            {validationIssues.length > 0 ? (
              <ul className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                {validationIssues.map((issue, i) => (
                  <li key={i} className={issue.level === 'error' ? 'text-red-800' : 'text-amber-900'}>
                    {issue.message}
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="mb-1 block text-sm font-medium">Bill to (client) *</label>
                <select
                  value={clientId}
                  onChange={(e) => setClientId(Number(e.target.value))}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                >
                  {billingClients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Invoice number *</label>
                <input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  data-soft-keyboard-handled
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Invoice date *</label>
                <input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="mb-1 flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={hasDueDate}
                    onChange={(e) => setHasDueDate(e.target.checked)}
                  />
                  Due date
                </label>
                <input
                  type="date"
                  value={dueDate}
                  disabled={!hasDueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Period from</label>
                <input
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Period to</label>
                <input
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">GST pricing</label>
                <select
                  value={gstMode}
                  onChange={(e) => setGstMode(e.target.value as PoGstMode)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                >
                  <option value="inclusive">GST inclusive</option>
                  <option value="exclusive">GST exclusive</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Payment status</label>
                <select
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value as InvoicePaymentStatus)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
            </div>
            <div className="overflow-hidden rounded-lg border">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left">Description</th>
                    <th className="px-2 py-2 text-right">Qty</th>
                    <th className="px-2 py-2 text-right">Unit price</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((line, index) => (
                    <tr key={line.lineId} className="border-t">
                      <td className="px-2 py-1">
                        <input
                          value={line.description}
                          onChange={(e) => updateLine(index, { description: e.target.value })}
                          data-soft-keyboard-handled
                          className="w-full rounded border px-2 py-1"
                        />
                      </td>
                      <InvoiceLineNumericFields
                        line={line}
                        onUpdate={(patch) => updateLine(index, patch)}
                      />
                      <td className="px-2 py-1">
                        <button type="button" onClick={() => setItems((p) => p.filter((_, i) => i !== index))}>
                          <TrashIcon className="h-4 w-4 text-red-500" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                onClick={() => setItems((p) => [...p, newInvoiceLine()])}
                className="flex w-full items-center justify-center gap-1 border-t py-2 text-sm text-pink-600"
              >
                <PlusIcon className="h-4 w-4" /> Add line
              </button>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-soft-keyboard-handled
                rows={2}
                className="w-full rounded-md border px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="border-t bg-gray-50 px-6 py-4">
            <div className="mb-3 text-sm">
              <p className="flex justify-between font-bold">
                <span>Total (inc. GST)</span>
                <span>${totals.totalIncGst.toFixed(2)}</span>
              </p>
            </div>
            <div className="flex justify-between gap-3">
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={!canPreview || previewLoading}
                  onClick={() => void openPreview()}
                  className="rounded-md border px-3 py-2 text-sm disabled:opacity-50"
                >
                  {previewLoading ? 'Loading…' : 'Preview'}
                </button>
                <button
                  type="button"
                  disabled={!canPreview || pdfLoading}
                  onClick={() => void handlePdf()}
                  className="rounded-md border px-3 py-2 text-sm disabled:opacity-50"
                >
                  {pdfLoading ? 'Preparing…' : 'Download PDF'}
                </button>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={onClose} className="rounded-md bg-gray-200 px-4 py-2">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || hasBlockingIssues(validationIssues)}
                  className="rounded-md bg-pink-600 px-4 py-2 text-white disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save invoice'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {previewOpen && previewViewModel ? (
        <ClientInvoicePreviewModal
          viewModel={previewViewModel}
          onClose={() => setPreviewOpen(false)}
          downloading={pdfLoading}
          onDownloadPdf={() => void handlePdf()}
        />
      ) : null}
    </div>
  );
};

export default ClientInvoiceBuilder;
